<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="order-statistics-page">
            <!-- 顶部头部 -->
            <div class="header-section">
                <div class="school-name">{{ selectedSchool?.name }}</div>
                <van-button type="primary" plain class="switch-school-btn" @click="showSchoolPicker = true">
                    <van-icon name="exchange" />
                    切换学校
                </van-button>
            </div>

            <!-- 日期筛选 -->
            <div class="date-filter-section">
                <div class="date-tabs">
                    <van-button v-for="tab in dateTabs" :key="tab.key"
                        :class="activeDateTab === tab.key ? 'date-tab active' : 'date-tab'"
                        @click="selectDateTab(tab.key)">
                        {{ tab.label }}
                    </van-button>
                </div>

                <div v-if="activeDateTab === 'custom'" class="custom-date-range">
                    <van-field v-model="dateRangeText" placeholder="请选择日期区间" readonly @click="showCalendar = true"
                        right-icon="calendar-o" class="date-input" />
                </div>
            </div>

            <!-- 统计卡片 -->
            <div class="stats-section">
                <div class="stats-grid">
                    <div class="stat-card retail">
                        <div class="stat-title">零售消费</div>
                        <div class="stat-value">¥{{ retailAmount }}</div>
                    </div>
                    <div class="stat-card milk">
                        <div class="stat-title">订奶实收</div>
                        <div class="stat-value">¥{{ milkAmount }}</div>
                    </div>
                    <div class="stat-card refund">
                        <div class="stat-title">退款金额</div>
                        <div class="stat-value">¥{{ refundAmount }}</div>
                    </div>
                </div>
            </div>

            <!-- 机器零售消费排行 -->
            <div class="ranking-section">
                <div class="section-title">机器零售消费排行</div>
                <!-- 空数据 -->
                <div class="empty-data" v-if="machineRanking.length === 0">
                    <van-empty description="暂无数据" />
                </div>
                <!-- 正常数据 -->
                <template v-else>
                    <div class="machine-list">
                        <div v-for="(machine, index) in machineRanking" :key="machine.id" class="machine-item">
                            <div class="machine-info">
                                <van-image :src="machine.image_1920" class="machine-image" fit="cover" />
                                <div class="machine-details">
                                    <div class="machine-name">{{ machine.name }}</div>
                                    <div class="machine-location">{{ machine.device_placement_address }}</div>
                                    <div class="sales-amount">¥{{ machine.net_consumption }}</div>
                                </div>
                            </div>
                            <div class="machine-sales">
                                <div class="rank-number">{{ index + 1 }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <!-- 学校选择器 -->
            <SearchablePicker v-model:show="showSchoolPicker" title="选择学校" :options="schoolOptions"
                :value="selectedSchool" value-key="id" display-key="name" @confirm="onSchoolConfirm"
                @cancel="onSchoolCancel" />

            <!-- 日期选择弹窗 -->
            <van-calendar v-model:show="showCalendar" type="range" :min-date="minDate" :max-date="maxDate" :allow-same-day="true"
                @confirm="onDateConfirm" color="#8B0000" />
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import SearchablePicker from '@/components/SearchablePicker.vue'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'

onMounted(() => {
    setWxTitle('订单统计')
    initPage()
})

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 响应式数据
const showSchoolPicker = ref(false)
const showCalendar = ref(false)
const schoolOptions = ref([])
const selectedSchool = ref(null)
const activeDateTab = ref('today')
const startDate = ref('')
const endDate = ref('')
const dateRangeText = ref('')

// 日期范围限制
const minDate = new Date(new Date().getFullYear() - 1, 0, 1)
const maxDate = new Date(new Date().getFullYear() + 1, 11, 31)

// 日期标签
const dateTabs = ref([
    { key: 'yesterday', label: '昨日' },
    { key: 'today', label: '今日' },
    { key: 'week', label: '本周' },
    { key: 'month', label: '本月' },
    { key: 'custom', label: '自定义' }
])

// 统计数据
const retailAmount = ref('0')
const milkAmount = ref('0')
const refundAmount = ref('0')

// 机器排行榜数据
const machineRanking = ref([
    {
        id: 1,
        name: 'A区-1号智能售货机',
        location: '教学楼一层西侧大厅',
        salesAmount: '1280.00',
        image: require('@/assets/head.png')
    },
    {
        id: 2,
        name: 'B区-2号智能售货机',
        location: '教学楼二层东侧走廊',
        salesAmount: '960.00',
        image: require('@/assets/head.png')
    },
    {
        id: 3,
        name: 'C区-1号智能售货机',
        location: '图书馆一层大厅',
        salesAmount: '620.00',
        image: require('@/assets/head.png')
    }
])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 页面初始化
const initPage = async () => {
    try {
        // 这里可以调用API获取订单统计数据
        const params = {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            partner_id: parseInt(localStorage.getItem('userId')),
            date_type: activeDateTab.value,
        }
        if (selectedSchool.value) {
            params.place_id = selectedSchool.value.id
        }
        if (startDate.value) {
            params.start_date = startDate.value
            params.end_date = endDate.value
        }
        const res = await post('/api/order/statistics', params)
        if (res.result && res.result.state === 'ok') {
            schoolOptions.value = res.result.place_list || []
            selectedSchool.value = res.result.selected_place_info || null
            // 处理统计数据
            retailAmount.value = res.result.retail_consumption || '0.00'
            milkAmount.value = res.result.milk_receipt || '0.00'
            refundAmount.value = res.result.refund_amount || '0.00'
            machineRanking.value = res.result.machine_ranking || []
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }

    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
    }
}

// 方法
const selectDateTab = (tabKey) => {
    activeDateTab.value = tabKey
    if (tabKey !== 'custom') {
        // 根据选择的标签设置日期范围
        console.log('选择日期标签:', tabKey)
        // 这里可以根据标签设置具体的日期范围
        initPage()
    }
}

const onSchoolConfirm = (value) => {
    selectedSchool.value = value
    showSchoolPicker.value = false
    console.log('选择学校:', value)
    // 这里可以根据选择的学校重新获取数据
    initPage()
}

const onSchoolCancel = () => {
    showSchoolPicker.value = false
}

// 日期确认处理函数
const onDateConfirm = (values) => {
    const [start, end] = values
    startDate.value = formatDate(start)
    endDate.value = formatDate(end)
    dateRangeText.value = `${startDate.value} 至 ${endDate.value}`
    showCalendar.value = false

    // 这里可以根据选择的日期范围重新获取数据
    console.log('选择的日期范围:', startDate.value, '至', endDate.value)
    initPage()
}

// 格式化日期
const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

</script>

<style scoped>
.order-statistics-page {
    background: #fff;
    min-height: 100vh;
    padding: 0 16px;
}

.header-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    padding: 16px;
}

.school-name {
    font-size: 18px;
    font-weight: 500;
}

.switch-school-btn {
    height: 34px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: 8px;
}

.date-filter-section {
    margin-bottom: 16px;
}

.date-tabs {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.date-tab {
    height: 36px;
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 9999px;
    background: #F3F4F6;
    color: #4B5563;
    border: none;
}

.date-tab.active {
    background: var(--van-primary-color);
    color: #fff;
}

.custom-date-range {
    margin-top: 12px;
}

.date-input {
    border-radius: 8px;
    border: 1px solid #e9ecef;
    text-align: center;
}

.date-input :deep(.van-field__control) {
    background: transparent;
    text-align: center;
}

.stats-section {
    margin-bottom: 24px;
}

.stats-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
}

.stat-card {
    padding: 12px;
    border-radius: 8px;
    width: 24%;
}

.stat-card.retail {
    background: #FEF2F2;
}

.stat-card.milk {
    background: #F0FDF4;
}

.stat-card.refund {
    background: #FEFCE8;
}

.stat-title {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 16px;
    font-weight: 500;
}

.stat-card.retail .stat-value {
    color: #8B0000;
}

.stat-card.milk .stat-value {
    color: #16A34A;
}

.stat-card.refund .stat-value {
    color: #CA8A04;
}

.ranking-section {
    padding-bottom: 20px;
}

.section-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 16px;
}

.machine-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.machine-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    border: 1px solid #f3f4f6;
}

.machine-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.machine-image {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    flex-shrink: 0;
}

.machine-details {
    flex: 1;
}

.machine-name {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
}

.machine-location {
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
}

.machine-sales {
    display: flex;
    align-items: center;
    gap: 12px;
}

.sales-amount {
    font-size: 16px;
    font-weight: 500;
    color: var(--van-primary-color);
}

.rank-number {
    font-size: 24px;
    font-weight: 600;
    color: #d1d5db;
    width: 32px;
    text-align: center;
}
</style>